'use client';

import React, { useRef, useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const featuredExperiences = [
  {
    id: 1,
    title: 'Sunrise Gorilla Trekking in Bwindi',
    host: 'Samuel Kato',
    duration: '6 hours',
    price: 120,
    rating: 4.98,
    reviews: 214,
    image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&q=80',
    alt: 'Mountain gorilla family in the misty Bwindi Impenetrable Forest at sunrise',
    tag: 'Bestseller',
    tagColor: 'bg-rose-500',
  },
  {
    id: 2,
    title: 'White Water Rafting on the Nile',
    host: 'Amara Osei',
    duration: '4 hours',
    price: 75,
    rating: 4.95,
    reviews: 389,
    image: 'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=600&q=80',
    alt: 'Rafters navigating powerful white water rapids on the Nile River in Jinja',
    tag: 'Top rated',
    tagColor: 'bg-blue-500',
  },
  {
    id: 3,
    title: 'Traditional Ugandan Cooking Class',
    host: 'Grace Namukasa',
    duration: '3 hours',
    price: 45,
    rating: 4.92,
    reviews: 156,
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
    alt: 'Hands-on traditional Ugandan cooking class with local ingredients and spices',
    tag: 'New',
    tagColor: 'bg-green-500',
  },
  {
    id: 5,
    title: 'Kampala Street Art & Culture Walk',
    host: 'Fatima Nakato',
    duration: '3 hours',
    price: 30,
    rating: 4.87,
    reviews: 203,
    image: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=600&q=80',
    alt: 'Vibrant street murals and local art installations in the heart of Kampala city',
    tag: 'Popular',
    tagColor: 'bg-purple-500',
  },
  {
    id: 8,
    title: 'Murchison Falls Boat Safari',
    host: 'Christine Akello',
    duration: '3 hours',
    price: 65,
    rating: 4.94,
    reviews: 178,
    image: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&q=80',
    alt: 'Boat safari on the Nile approaching the thundering Murchison Falls with hippos nearby',
    tag: 'Bestseller',
    tagColor: 'bg-rose-500',
  },
];

export default function ExperiencesSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-10 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className={`mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Experiences</h2>
            <p className="text-gray-500 mt-1">Unique activities hosted by local experts</p>
          </div>
          <Link href="/experiences" className="text-sm font-semibold text-gray-900 underline underline-offset-2 hover:text-rose-500 transition-colors hidden md:block">
            Show all experiences
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {featuredExperiences?.map((exp, i) => (
          <Link
            key={exp?.id}
            href={`/experiences/${exp?.id}`}
            className={`group cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <div className="relative h-52 rounded-2xl overflow-hidden mb-3">
              <AppImage
                src={exp?.image}
                alt={exp?.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className={`absolute top-3 left-3 ${exp?.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
                {exp?.tag}
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white font-bold text-sm line-clamp-1">{exp?.title}</p>
                <p className="text-white/70 text-xs mt-0.5">by {exp?.host}</p>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{exp?.duration}</span>
                <div className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-rose-500 fill-rose-500" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs font-semibold text-gray-800">{exp?.rating}</span>
                  <span className="text-xs text-gray-400">({exp?.reviews})</span>
                </div>
              </div>
              <p className="text-sm font-bold text-gray-900 mt-1">
                <span className="text-rose-500">${exp?.price}</span>
                <span className="text-gray-400 font-normal text-xs"> / person</span>
              </p>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-5 md:hidden">
        <Link href="/experiences" className="text-sm font-semibold text-gray-900 underline underline-offset-2 hover:text-rose-500 transition-colors">
          Show all experiences
        </Link>
      </div>
    </section>
  );
}
